import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
/**
 * Shared share-with-three controls used by:
 *   - /case-study CTA at the bottom of the page
 *   - /results locked-cohort skeleton
 *   - (any other place we want a uniform "Share on X / Copy link" pair)
 *
 * Builds the share URL from the user's share_code so the recipient lands on
 * the survey root with their answer attributed to this user's cohort:
 *   https://<host>/?ref=<shareCode>
 *
 * When `shareCode` is null (e.g. the viewer's cookies didn't let the server
 * resolve their share_code), we fall back to a bare site URL with no
 * attribution. The buttons still work; the recipient just won't be linked
 * back to the user's cohort tree. The `<ShareCodeMissingHint />` element
 * surfaces this path so the user knows attribution is off.
 */
export function SharePanel({ shareCode, tweetText, variant = "default", }) {
    const [copied, setCopied] = useState(false);
    const shareUrl = typeof window === "undefined"
        ? ""
        : shareCode
            ? `${window.location.origin}/?ref=${shareCode}`
            : `${window.location.origin}/`;
    const tweetUrl = "https://twitter.com/intent/tweet?text=" +
        encodeURIComponent(tweetText) +
        "&url=" +
        encodeURIComponent(shareUrl);
    async function copy() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        }
        catch {
            /* clipboard API unavailable — surface the URL inline as fallback */
        }
    }
    return (_jsxs("div", { className: `share-panel share-panel--${variant}`, children: [_jsxs("div", { className: "share-panel-actions", children: [_jsx("a", { className: "share-twitter", href: tweetUrl, target: "_blank", rel: "noopener noreferrer", "aria-label": `Share on X. Opens in a new tab. Tweet text: ${tweetText}`, children: "Share on X" }), _jsx("button", { type: "button", className: "share-copy", onClick: copy, "aria-live": "polite", children: copied ? "Link copied" : "Copy link" })] }), _jsx("div", { className: "share-panel-url", "aria-label": "Your share URL", children: shareUrl }), shareCode === null && (_jsxs("p", { className: "share-panel-warning", children: ["We couldn\u2019t attribute this share to your cohort. Open", " ", _jsx("a", { href: "/results", children: "your results" }), " to refresh your session, then come back."] }))] }));
}
